// src/components/AtaPdf.jsx
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const MARGEM = 15;
const BLOCO_LARGURA_PX = 714;

class AtaPdf {
  static async exportar(opcao, projetoNome, notaNome, dataLocal, pauta, local, texto, participantes, objetivos = [], observacao = '') {
    const loadingMsg = this.criarMensagemCarregamento();
    document.body.appendChild(loadingMsg);
    try {
      const blob = await this.gerarPDFSemCortes(
        opcao, projetoNome, notaNome, dataLocal, pauta, local, texto, participantes, objetivos, observacao
      );

      this.removerMensagemCarregamento(loadingMsg);

      const nomeSeguro = this.sanitizarNomeArquivo(projetoNome);
      const nomeNota = this.sanitizarNomeArquivo(notaNome);
      const sufixo = opcao === 'texto' ? 'texto' : opcao === 'objetivos' ? 'objetivos' : 'completo';
      const nomeArquivo = `ATA - ${nomeSeguro} - ${nomeNota} - ${sufixo}.pdf`;

      this.baixarArquivo(blob, nomeArquivo);
      alert('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      this.removerMensagemCarregamento(loadingMsg);
      alert(`Erro ao gerar PDF: ${error.message}`);
    }
  }

  static async gerarBase64(opcao, projetoNome, notaNome, dataLocal, pauta, local, texto, participantes, objetivos = [], observacao = '') {
  const blob = await this.gerarPDFSemCortes(
    opcao, projetoNome, notaNome, dataLocal, pauta, local, texto, participantes, objetivos, observacao
  );
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

  static async gerarPDFSemCortes(opcao, projetoNome, notaNome, dataLocal, pauta, local, texto, participantes, objetivos, observacao) {
    const blocosEl = [];

    blocosEl.push(this.criarBlocoHeader(opcao, projetoNome, notaNome, dataLocal, pauta, local, participantes));

    if ((opcao === 'texto' || opcao === 'completo') && texto?.trim()) {
      blocosEl.push(this.criarBlocoTexto(texto));
    }

    // ── Bloco de observação (sempre que houver conteúdo) ──
    if (observacao?.trim()) {
      blocosEl.push(this.criarBlocoObservacao(observacao));
    }

    if ((opcao === 'objetivos' || opcao === 'completo') && objetivos?.length > 0) {
      blocosEl.push(this.criarBlocoTituloObjetivos());
      objetivos.forEach((obj, index) => {
        blocosEl.push(this.criarBlocoObjetivo(obj, index));
      });
    }

        const stage = document.createElement('div');
    stage.style.cssText = `
      position: fixed;
      left: -9999px;
      top: 0;
      width: ${BLOCO_LARGURA_PX}px;
      background: #fff;
    `;
    document.body.appendChild(stage);

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const larguraUtilMm = pdfWidth - MARGEM * 2;
    const alturaUtilMm = pdfHeight - MARGEM * 2;

    // ── Captura cada bloco individualmente (evita canvas gigante e estouro de limite do navegador) ──
    const blocosCanvas = [];
    for (const bloco of blocosEl) {
      stage.appendChild(bloco);
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvasBloco = await html2canvas(bloco, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: BLOCO_LARGURA_PX,
        windowWidth: BLOCO_LARGURA_PX,
      });
      const escalaMmPorPxBloco = larguraUtilMm / canvasBloco.width;
      const alturaMm = canvasBloco.height * escalaMmPorPxBloco;
      blocosCanvas.push({ canvas: canvasBloco, alturaMm, escalaMmPorPxBloco });
      stage.removeChild(bloco);
    }

    document.body.removeChild(stage);

    // ── Paginação: empacota blocos por página, sub-divide blocos maiores que uma página inteira ──
    let yAtualMm = 0;
    let paginaIniciada = false;

    const novaPagina = () => {
      if (paginaIniciada) pdf.addPage();
      paginaIniciada = true;
      yAtualMm = 0;
    };

    for (const { canvas, alturaMm, escalaMmPorPxBloco } of blocosCanvas) {
      if (alturaMm > alturaUtilMm) {
        const alturaUtilPx = alturaUtilMm / escalaMmPorPxBloco;
        let inicioPx = 0;
        while (inicioPx < canvas.height) {
          const alturaFatiaPx = Math.min(alturaUtilPx, canvas.height - inicioPx);
          novaPagina();
          const canvasFatia = document.createElement('canvas');
          canvasFatia.width = canvas.width;
          canvasFatia.height = alturaFatiaPx;
          const ctx = canvasFatia.getContext('2d');
          ctx.drawImage(canvas, 0, inicioPx, canvas.width, alturaFatiaPx, 0, 0, canvas.width, alturaFatiaPx);
          const imgData = canvasFatia.toDataURL('image/png', 0.95);
          const imgHeightMm = alturaFatiaPx * escalaMmPorPxBloco;
          pdf.addImage(imgData, 'PNG', MARGEM, MARGEM, larguraUtilMm, imgHeightMm, undefined, 'FAST');
          inicioPx += alturaFatiaPx;
          yAtualMm = imgHeightMm;
        }
        continue;
      }

      if (!paginaIniciada || yAtualMm + alturaMm > alturaUtilMm) {
        novaPagina();
      }

      const imgData = canvas.toDataURL('image/png', 0.95);
      pdf.addImage(imgData, 'PNG', MARGEM, MARGEM + yAtualMm, larguraUtilMm, alturaMm, undefined, 'FAST');
      yAtualMm += alturaMm;
    }

    const totalPaginas = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPaginas; i++) {
      pdf.setPage(i);

      // Marca OnBox (esquerda)
      pdf.setFontSize(8);
      pdf.setTextColor(30, 58, 138); // mesma cor azul do cabeçalho (#1e3a8a)
      pdf.setFont(undefined, 'bold');
      pdf.text('OnBox', MARGEM, pdfHeight - MARGEM / 2);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(150, 150, 150);
      pdf.text('· Gerado por OnBox', MARGEM + pdf.getTextWidth('OnBox') + 2, pdfHeight - MARGEM / 2);

      // Número de página (centro)
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Página ${i} de ${totalPaginas}`, pdfWidth / 2, pdfHeight - MARGEM / 2, { align: 'center' });
    }

    const tituloPDF = `ATA - ${projetoNome} - ${notaNome} (${opcao === 'texto' ? 'Texto' : opcao === 'objetivos' ? 'Objetivos' : 'Completo'})`;
    pdf.setProperties({
      title: tituloPDF,
      subject: 'Ata de Reunião',
      author: 'Sistema OnBox',
      keywords: 'ata, reunião, objetivos',
      creator: 'OnBox',
    });

    return pdf.output('blob');
  }

  // ─── Escape + parser de formatação (espelha o editor: /*tópico*/, **negrito**, *itálico*) ───

  static escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  static formatarTextoAtaParaPdf(raw) {
    if (!raw) return '';
    const FORMAT_REGEX = /\/\*([^\n]+?)\*\/|\*\*([^\n]+?)\*\*|\*([^\n*]+?)\*/g;
    const esc = this.escapeHtml;

    let html = '';
    let cursor = 0;
    let m;
    FORMAT_REGEX.lastIndex = 0;
    while ((m = FORMAT_REGEX.exec(raw)) !== null) {
      html += esc(raw.slice(cursor, m.index)).replace(/\n/g, '<br>');
      if (m[1] !== undefined) {
        html += `<div style="font-weight:700;margin:12px 0 4px;color:#1e293b;">` +
                  `<span style="color:#1e40af;margin-right:6px;">&#8226;</span>${esc(m[1])}` +
                `</div>`;
      } else if (m[2] !== undefined) {
        html += `<strong>${esc(m[2])}</strong>`;
      } else {
        html += `<em>${esc(m[3])}</em>`;
      }
      cursor = m.index + m[0].length;
    }
    html += esc(raw.slice(cursor)).replace(/\n/g, '<br>');
    return html;
  }

  static formatarInlinePdf(raw) {
    if (!raw) return '';
    const INLINE_REGEX = /\*\*([^\n]+?)\*\*|\*([^\n*]+?)\*/g;
    const esc = this.escapeHtml;

    let html = '';
    let cursor = 0;
    let m;
    INLINE_REGEX.lastIndex = 0;
    while ((m = INLINE_REGEX.exec(raw)) !== null) {
      html += esc(raw.slice(cursor, m.index));
      html += m[1] !== undefined ? `<strong>${esc(m[1])}</strong>` : `<em>${esc(m[2])}</em>`;
      cursor = m.index + m[0].length;
    }
    html += esc(raw.slice(cursor));
    return html;
  }

  // ─── Construtores de blocos individuais ───

  static criarBlocoHeader(opcao, projetoNome, notaNome, dataLocal, pauta, local, participantes) {
    const container = document.createElement('div');
    container.style.cssText = `
      width: ${BLOCO_LARGURA_PX}px;
      background: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      color: #333;
      box-sizing: border-box;
      padding: 0 4px;
      text-align: left;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      text-align: center;
      margin-bottom: 25px;
      border-bottom: 2px solid #1e3a8a;
      padding-bottom: 18px;
    `;
    const titulo = document.createElement('h1');
    titulo.style.cssText = `font-size: 24px; color: #1e3a8a; margin: 0 0 10px 0;`;
    titulo.textContent = 'ATA DE REUNIÃO';
    const subtitulo = document.createElement('div');
    subtitulo.style.cssText = `font-size: 16px; color: #4b5563; margin-top: 5px;`;
    subtitulo.innerHTML = `
      <div><strong>Projeto:</strong> ${projetoNome || 'Não informado'}</div>
      <div><strong>Nota:</strong> ${notaNome || 'Não informado'}</div>
      ${dataLocal ? `<div><strong>Data:</strong> ${dataLocal}</div>` : ''}
    `;
    header.appendChild(titulo);
    header.appendChild(subtitulo);
    container.appendChild(header);

    if (pauta) {
      const pautaEl = document.createElement('div');
      pautaEl.style.cssText = `font-size: 20px; font-weight: bold; margin-bottom: 8px; color: #1e40af;`;
      pautaEl.textContent = pauta;
      container.appendChild(pautaEl);
    }
    if (local) {
      const localEl = document.createElement('div');
      localEl.style.cssText = `font-size: 16px; color: #4b5563; margin-bottom: 15px;`;
      localEl.textContent = `Local: ${local}`;
      container.appendChild(localEl);
    }

    if (participantes?.length > 0) {
      const participantesEl = document.createElement('div');
      participantesEl.style.cssText = `margin-bottom: 10px;`;
      const tituloPart = document.createElement('h3');
      tituloPart.style.cssText = `font-size: 18px; color: #1e3a8a; margin: 0 0 10px 0;`;
      tituloPart.textContent = 'Participantes:';
      participantesEl.appendChild(tituloPart);

      const listaPart = document.createElement('div');
      listaPart.style.cssText = `display: flex; flex-wrap: wrap; gap: 8px;`;
      participantes.forEach(p => {
        const chip = document.createElement('span');
        chip.style.cssText = `
          background: #eef2ff; color: #1e3a8a; padding: 4px 10px;
          border: 1px solid #c7d2fe; border-radius: 16px; font-size: 14px; display: inline-block;
          margin-right: 6px; margin-bottom: 6px;
        `;
        chip.textContent = `${p.nome}${p.funcao ? ` (${p.funcao})` : ''}`;
        listaPart.appendChild(chip);
      });
      participantesEl.appendChild(listaPart);
      container.appendChild(participantesEl);
    }

    return container;
  }

  static criarBlocoTexto(texto) {
    const container = document.createElement('div');
    container.style.cssText = `
      width: ${BLOCO_LARGURA_PX}px;
      background: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      color: #1f2937;
      box-sizing: border-box;
      padding: 0 4px;
      text-align: left;
      line-height: 1.8;
      font-size: 14px;
    `;
    container.innerHTML = this.formatarTextoAtaParaPdf(texto);
    return container;
  }

  // ─── NOVO: Bloco de observação ───
  static criarBlocoObservacao(observacao) {
    const container = document.createElement('div');
    container.style.cssText = `
      width: ${BLOCO_LARGURA_PX}px;
      background: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      box-sizing: border-box;
      padding: 0 4px;
    `;

    const bloco = document.createElement('div');
    bloco.style.cssText = `
      padding: 15px;
      border-left: 4px solid #eab308;
      background: #fefce8;
      border-radius: 0 6px 6px 0;
      box-sizing: border-box;
    `;

    const titulo = document.createElement('div');
    titulo.style.cssText = `font-size: 15px; font-weight: bold; color: #a16207; margin-bottom: 6px;`;
    titulo.textContent = 'Observação';

    const corpo = document.createElement('div');
    corpo.style.cssText = `font-size: 14px; color: #1f2937; line-height: 1.6; text-align: left; white-space: pre-wrap;`;
    corpo.textContent = observacao;

    bloco.appendChild(titulo);
    bloco.appendChild(corpo);
    container.appendChild(bloco);
    return container;
  }

  static criarBlocoTituloObjetivos() {
    const container = document.createElement('div');
    container.style.cssText = `
      width: ${BLOCO_LARGURA_PX}px;
      background: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      padding: 0 4px;
    `;
    const tituloObj = document.createElement('h2');
    tituloObj.style.cssText = `
      font-size: 20px; color: #1e3a8a; margin: 0;
      padding-bottom: 10px; border-bottom: 2px solid #93c5fd;
    `;
    tituloObj.textContent = 'Objetivos Definidos';
    container.appendChild(tituloObj);
    return container;
  }

  static criarBlocoObjetivo(obj, index) {
    const container = document.createElement('div');
    container.style.cssText = `
      width: ${BLOCO_LARGURA_PX}px;
      background: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      box-sizing: border-box;
      padding: 4px 4px 4px 0;
    `;

    const objEl = document.createElement('div');
    objEl.style.cssText = `
      padding: 15px;
      border-left: 4px solid ${obj.concluido ? '#10b981' : '#3b82f6'};
      background: ${obj.concluido ? '#ecfdf5' : '#f0f9ff'};
      border-radius: 0 6px 6px 0;
      box-sizing: border-box;
    `;

    const conteudo = document.createElement('div');
    conteudo.style.cssText = `display: flex; flex-direction: column; gap: 8px;`;

    const linha1 = document.createElement('div');
    linha1.style.cssText = `display: flex; align-items: flex-start; gap: 10px;`;
    const numero = document.createElement('span');
    numero.style.cssText = `
      min-width: 24px; height: 24px;
      background: ${obj.concluido ? '#10b981' : '#3b82f6'};
      color: white; border-radius: 50%; display: flex;
      align-items: center; justify-content: center; font-weight: bold; font-size: 14px;
    `;
    numero.textContent = index + 1;
    const textoObj = document.createElement('span');
    textoObj.style.cssText = `
      flex: 1; font-size: 15px; text-align: left;
      ${obj.concluido ? 'text-decoration: line-through; color: #065f46;' : ''}
    `;
    textoObj.innerHTML = this.formatarInlinePdf(obj.texto);
    linha1.appendChild(numero);
    linha1.appendChild(textoObj);
    conteudo.appendChild(linha1);

    if (obj.responsaveis?.length > 0) {
      const respEl = document.createElement('div');
      respEl.style.cssText = `font-size: 13px; color: #4b5563;`;
      respEl.innerHTML = `<strong>Responsáveis:</strong> ${obj.responsaveis.map(r => this.escapeHtml(r.nome_exibicao || r.nome || 'Usuário')).join(', ')}`;
      conteudo.appendChild(respEl);
    }

    if (obj.dataEntrega) {
      const dataEl = document.createElement('div');
      dataEl.style.cssText = `font-size: 13px; color: #4b5563;`;
      const dataFormatada = obj.dataEntrega.split('-').reverse().join('/');
      dataEl.innerHTML = `<strong>Data de entrega:</strong> ${dataFormatada}`;
      conteudo.appendChild(dataEl);
    }

    const statusEl = document.createElement('div');
    statusEl.style.cssText = `font-size: 13px; font-weight: bold; color: ${obj.concluido ? '#065f46' : '#1e40af'};`;
    statusEl.innerHTML = obj.concluido
      ? `<span style="color: #10b981;">✓ Concluído em ${obj.concluidoEm ? new Date(obj.concluidoEm).toLocaleDateString('pt-BR') : 'data não informada'}</span>`
      : `<span style="color: #3b82f6;">Em andamento</span>`;
    conteudo.appendChild(statusEl);

    // ── NOVO: Bloco de comentários (substitui o antigo obj.comentario) ──
    if (obj.comentarios?.length > 0) {
      const comentariosWrap = document.createElement('div');
      comentariosWrap.style.cssText = `margin-top: 4px; display: flex; flex-direction: column; gap: 6px;`;

      const tituloComentarios = document.createElement('div');
      tituloComentarios.style.cssText = `font-size: 12px; font-weight: bold; color: #4b5563;`;
      tituloComentarios.textContent = 'Comentários:';
      comentariosWrap.appendChild(tituloComentarios);

      obj.comentarios.forEach(c => {
        const comentarioEl = document.createElement('div');
        comentarioEl.style.cssText = `
          padding: 8px 12px; background: #f3f4f6;
          border-radius: 6px; font-size: 13px; color: #374151; text-align: left;
        `;
        const dataFormatada = c.data ? new Date(c.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
        comentarioEl.innerHTML = `<strong>${this.escapeHtml(c.autor)}</strong> <span style="color:#9ca3af;font-size:11px;">${dataFormatada}</span><div>${this.escapeHtml(c.texto)}</div>`;
        comentariosWrap.appendChild(comentarioEl);
      });

      conteudo.appendChild(comentariosWrap);
    }

    objEl.appendChild(conteudo);
    container.appendChild(objEl);
    return container;
  }

  static criarMensagemCarregamento() {
    const loadingMsg = document.createElement('div');
    loadingMsg.id = 'ata-pdf-loading';
    loadingMsg.style.cssText = `
      position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
      background: white; padding: 20px 40px; border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 10000;
      font-size: 16px; font-weight: bold; text-align: center;
    `;
    loadingMsg.innerHTML = `
      <div>Gerando PDF...</div>
      <div style="font-size: 12px; font-weight: normal; margin-top: 8px; color: #666;">
        Isso pode levar alguns segundos
      </div>
    `;
    return loadingMsg;
  }

  static removerMensagemCarregamento(elemento) {
    try {
      if (elemento && elemento.parentNode) elemento.parentNode.removeChild(elemento);
    } catch (err) {
      console.warn('Erro ao remover mensagem de carregamento:', err);
    }
  }

  static baixarArquivo(blob, nomeArquivo) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  static sanitizarNomeArquivo(nome) {
    if (!nome) return 'documento';
    return nome
      .replace(/[^a-zA-Z0-9_\-\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50);
  }
}

export default AtaPdf;